/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { graphql } from 'react-apollo';

import type { ApolloError } from 'apollo-client';

import useRouter from 'js/lib/useRouter';
import { get as userGet } from 'js/lib/user';

import { FullscreenDialog } from '@coursera/cds-core';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import type {
  CoursesWithNaptimeSessionsQueryQuery,
  CoursesWithNaptimeSessionsQueryQueryVariables,
} from 'bundles/degree-home/components/SessionJoinDialog/utils/__queries__/__generated__/CoursesWithNaptimeSessionsQuery';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/degree-home';

import EnrollmentConfirmationView from './EnrollmentConfirmationView';
import ErrorView from './ErrorView';
import NoEnrollmentRequiredView from './NoEnrollmentRequiredView';
import PickStartDateView from './PickStartDateView';
import type { EnrollableCourse, EnrollableSession } from './types/EnrollableCourse';
import type { EnrollingCourse } from './types/EnrollingCourse';
import CoursesWithNaptimeSessionsQuery from './utils/__queries__/CoursesWithNaptimeSessionsQuery.graphql';
import { enrollInMultipleCourses } from './utils/enrollInMultipleCourses';

type SessionJoinDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  // courseIds is used inside graphQL()
  courseIds: string[];
  courseIdsLoading?: boolean; // Allows CoursesWithNaptimeSessionsQuery to be delayed while courseIds are loading in a parent component
  onEnrolled?: () => void;
  enrollingCourseId?: string;
};

type PropsFromQuery = {
  enrollableCourses?: EnrollableCourse[] | null;
  enrollableCoursesLoading?: boolean;
  enrollableCoursesError?: ApolloError;
};

const styles = {
  courseraLogo: () => css`
    width: 100px;
    margin-top: var(--cds-spacing-100);
  `,
  loadingIconWrapper: css`
    margin: 2rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  `,
  noCoursesMessage: () => css`
    padding: var(--cds-spacing-400) 0;
  `,
};

export const SessionJoinDialog = ({
  isOpen,
  onClose,
  onEnrolled,
  enrollingCourseId,
  enrollableCourses,
  enrollableCoursesLoading,
  enrollableCoursesError,
  courseIdsLoading,
}: SessionJoinDialogProps & PropsFromQuery) => {
  const { Header, Content } = FullscreenDialog;
  const [successfulEnrollments, setSuccessfulEnrollments] = useState<Array<EnrollingCourse>>([]);
  const [enrollmentErrorMsg, setEnrollmentErrorMsg] = useState('');

  const router = useRouter();

  const handleClose = () => {
    onClose();
    setSuccessfulEnrollments([]);
    setEnrollmentErrorMsg('');
  };

  const handleEnroll = async (enrollingCourses: EnrollingCourse[]) => {
    try {
      const enrolledCourses = await enrollInMultipleCourses(enrollingCourses);
      setSuccessfulEnrollments(enrolledCourses);
    } catch {
      setEnrollmentErrorMsg(_t('We can’t seem to enroll you into your selected courses right now, please try again.'));
    }

    if (onEnrolled) onEnrolled();
  };

  if (enrollableCoursesError || enrollmentErrorMsg) {
    return (
      <FullscreenDialog
        open={isOpen}
        onClose={handleClose}
        aria-describedby={_t('Session enrollment dialog')}
        contentWidth="60%"
        sticky="actions"
      >
        <Header />
        <TrackedDiv withVisibilityTracking trackingName="enrollment_dialog" />
        <ErrorView headingText={enrollmentErrorMsg && _t('Something went wrong')} bodyText={enrollmentErrorMsg} />
      </FullscreenDialog>
    );
  }

  const isLoading = courseIdsLoading || enrollableCoursesLoading;
  const hasEnrollableCourses = enrollableCourses && enrollableCourses.length > 0;
  const hasSuccesfullyEnrolled = successfulEnrollments.length > 0;
  /*
   * There are three situations here:
   * 1. enrollingCourse exists but it is already enrolled
   * 2. enrollingCourse does not exist, might caused by modify the course id in the url directly
   * 3. enrollingCourse exists and it is not enrolled yet
   
   */
  const enrollingCourse = enrollableCourses?.find((course: EnrollableCourse) => course.id === enrollingCourseId);
  const isEnrollingCourseEnrolled =
    enrollingCourseId && enrollingCourse?.activeAndUpcomingSessions?.elements.some((session) => session?.isEnrolled);

  /**
   * If situation 2 is met, change the url params from the incorrect course ID to simply showEnrollmentDialog=true
   */
  if (!isLoading && !enrollingCourse && enrollingCourseId) {
    const newQueryParams = { ...router.location.query };

    delete newQueryParams.enrollingCourseId;

    router.replace({
      ...router.location,
      params: router.params,
      query: { ...newQueryParams, showEnrollmentDialog: true },
    });
  }

  return (
    <FullscreenDialog
      open={isOpen}
      onClose={handleClose}
      aria-describedby={_t('Session enrollment dialog')}
      contentWidth="60%"
      sticky="actions"
    >
      <Header />

      <TrackedDiv withVisibilityTracking trackingName="enrollment_dialog" />
      {!hasSuccesfullyEnrolled && (
        <>
          {isLoading && (
            <Content aria-live="polite" aria-atomic="true">
              <div css={styles.loadingIconWrapper}>
                <SvgLoaderSignal title={_t('Loading.')} />
                <div>{_t('Loading sessions ...')}</div>
              </div>
            </Content>
          )}
          {!isLoading && !hasEnrollableCourses && (
            <NoEnrollmentRequiredView
              onClose={handleClose}
              headingText={_t('Nothing to do here! There are no courses available to enroll into right now.')}
              bodyText=""
            />
          )}
          {/* If situation 1 is met, display already enrolled view */}
          {!isLoading && isEnrollingCourseEnrolled && <NoEnrollmentRequiredView onClose={handleClose} />}
          {!isLoading && hasEnrollableCourses && !isEnrollingCourseEnrolled && (
            <PickStartDateView
              onEnroll={handleEnroll}
              onClose={handleClose}
              courses={enrollableCourses satisfies EnrollableCourse[]}
              enrollingCourseId={enrollingCourseId}
            />
          )}
        </>
      )}

      {hasSuccesfullyEnrolled && (
        <>
          <EnrollmentConfirmationView enrollments={successfulEnrollments} onClose={handleClose} />
        </>
      )}
    </FullscreenDialog>
  );
};

export default graphql<
  SessionJoinDialogProps,
  CoursesWithNaptimeSessionsQueryQuery,
  CoursesWithNaptimeSessionsQueryQueryVariables,
  PropsFromQuery
>(CoursesWithNaptimeSessionsQuery, {
  options: ({ courseIds, enrollingCourseId }: SessionJoinDialogProps) => {
    const courseIdsList = enrollingCourseId ? Array.from(new Set([...courseIds, enrollingCourseId])) : courseIds;
    return {
      // Ensure both query parts get the same course list, in the same order.
      // (The Naptime connection doesn't include a course id field in this query.)
      variables: {
        courseIds: courseIdsList,
        courseIdStrings: courseIdsList,
        learnerId: userGet().id.toString(),
      },
    };
  },
  // We don't need to send this request unless there are enrollable courses specified
  skip: ({ courseIds, enrollingCourseId, courseIdsLoading }) =>
    courseIdsLoading || (!enrollingCourseId && courseIds.length < 1),
  props: ({ data }) => {
    const queryCourses = data?.Course?.queryByIds ?? [];
    const learnerSessions = data?.NaptimeQueries?.OnDemandLearnerSessionsV1Resource?.activeAndUpcoming?.elements ?? [];

    const enrollableCourses = queryCourses
      .map((course, idx): EnrollableCourse | null => {
        const upcomingCourseSession = course.upcomingCourseSession;
        if (!upcomingCourseSession) return null;

        const enrollment = learnerSessions[idx];
        const isEnrolled = enrollment?.isEnrolled ?? false;
        const isEnrollableNow = enrollment?.isEnrollableNow ?? false;

        const session: EnrollableSession = {
          id: upcomingCourseSession.id,
          startsAt: Number(upcomingCourseSession.startsAt),
          endsAt: Number(upcomingCourseSession.endsAt),
          isEnrolled,
          isEnrollableNow,
        };

        return {
          id: course.id,
          slug: course.slug,
          name: course.name,
          activeAndUpcomingSessions: { elements: [session] },
        };
      })
      .filter((course): course is EnrollableCourse => Boolean(course));

    return {
      enrollableCoursesLoading: data?.loading as boolean,
      enrollableCoursesError: data?.error,
      enrollableCourses,
    };
  },
})(SessionJoinDialog);
